import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { css, useTheme } from '@emotion/react';
import { Fragment, useEffect, useMemo, useRef, useState, } from 'react';
import useLatest from 'use-latest';
import usePrevious from 'use-previous';
import { usePopper } from 'react-popper';
import isPropValid from '@emotion/is-prop-valid';
import { useClickTrigger } from '@sumup/collector';
import styled from '../../styles/styled';
import { listItem, shadow, typography } from '../../styles/style-mixins';
import { uniqueId } from '../../util/id';
import { useClickEvent } from '../../hooks/useClickEvent';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useFocusList } from '../../hooks/useFocusList';
import { isArrowDown, isArrowUp } from '../../util/key-codes';
import { useComponents } from '../ComponentsContext';
import Portal from '../Portal';
import Hr from '../Hr';
import { useStackContext } from '../StackContext';
import { isFunction } from '../../util/type-check';
const itemWrapperStyles = () => css `
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`;
const PopoverItemWrapper = styled('button', {
    shouldForwardProp: isPropValid,
})(listItem, itemWrapperStyles, typography('one'));
const iconStyles = (theme) => css `
  margin-right: ${theme.spacings.kilo};
`;
export const PopoverItem = ({ children, icon: Icon, onClick, tracking, ...props }) => {
    const components = useComponents();
    const Link = components.Link;
    const handleClick = useClickEvent(onClick, tracking, 'popover-item');
    return (_jsxs(PopoverItemWrapper, { as: props.href ? Link : 'button', onClick: handleClick, role: "menuitem", ...props, children: [Icon && _jsx(Icon, { css: iconStyles, size: "24" }, void 0), children] }, void 0));
};
const TriggerElement = styled.div `
  display: inline-block;
`;
const menuBaseStyles = ({ theme }) => css `
  padding: ${theme.spacings.byte} 0px;
  border: 1px solid ${theme.colors.n200};
  box-sizing: border-box;
  border-radius: ${theme.borderRadius.byte};
  background-color: ${theme.colors.white};
  visibility: hidden;
  opacity: 0;

  ${theme.mq.untilKilo} {
    opacity: 1;
    transform: translateY(100%);
    transition: transform ${theme.transitions.default},
      visibility ${theme.transitions.default};
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
`;
const menuOpenStyles = ({ theme, isOpen }) => isOpen &&
    css `
    opacity: 1;
    visibility: inherit;

    ${theme.mq.untilKilo} {
      transform: translateY(0);
    }
  `;
const PopoverMenu = styled('div')(shadow, menuBaseStyles, menuOpenStyles);
const dividerStyles = (theme) => css `
  margin: ${theme.spacings.byte} ${theme.spacings.mega};
  width: calc(100% - ${theme.spacings.mega}*2);
`;
const overlayStyles = ({ theme }) => css `
  ${theme.mq.untilKilo} {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${theme.colors.overlay};
    visibility: hidden;
    opacity: 0;
    transition: opacity ${theme.transitions.default},
      visibility ${theme.transitions.default};
  }
`;
const overlayOpenStyles = ({ theme, isOpen }) => isOpen &&
    css `
    ${theme.mq.untilKilo} {
      visibility: inherit;
      opacity: 1;
    }
  `;
const Overlay = styled.div(overlayStyles, overlayOpenStyles);
const popperStyles = ({ isOpen }) => css `
  pointer-events: ${isOpen ? 'all' : 'none'};
`;
const Popper = styled.div(popperStyles);
function isDivider(action) {
    return 'type' in action && action.type === 'divider';
}
export const Popover = ({ isOpen = false, onToggle, actions, placement = 'bottom', fallbackPlacements = ['top', 'right', 'left'], component: Component, modifiers = [], tracking, ...props }) => {
    const theme = useTheme();
    const zIndex = useStackContext();
    const triggerKey = useRef(null);
    const triggerEl = useRef(null);
    const menuEl = useRef(null);
    const triggerId = useMemo(() => uniqueId('trigger_'), []);
    const menuId = useMemo(() => uniqueId('popover_'), []);
    const sendEvent = useClickTrigger();
    const handleToggle = (state) => {
        onToggle((prev) => {
            const next = isFunction(state) ? state(prev) : state;
            if (tracking && tracking.label) {
                sendEvent({
                    component: 'popover',
                    ...tracking,
                    label: `${tracking.label}|${next ? 'open' : 'close'}`,
                });
            }
            return next;
        });
    };
    // Custom Popper.js modifier that switches to a bottom sheet on mobile.
    // useMemo prevents a render loop:
    // https://popper.js.org/react-popper/v2/faq/#why-i-get-render-loop-whenever-i-put-a-function-inside-the-popper-configuration
    const mobilePosition = useMemo(() => ({
        name: 'mobilePosition',
        enabled: true,
        phase: 'write',
        fn({ state }) {
            if (window.matchMedia(`${theme.breakpoints.untilKilo}`).matches) {
                // eslint-disable-next-line no-param-reassign
                state.styles.popper = {
                    width: '100%',
                    left: '0px',
                    right: '0px',
                    bottom: '0px',
                    position: 'fixed',
                    zIndex: (zIndex || theme.zIndex.popover).toString(),
                };
            }
            else {
                // eslint-disable-next-line no-param-reassign
                state.styles.popper.width = 'auto';
                // eslint-disable-next-line no-param-reassign
                state.styles.popper.zIndex = (zIndex || theme.zIndex.popover).toString();
            }
        },
    }), [theme, zIndex]);
    // Flip the popover if there is no space for the default placement.
    // https://popper.js.org/docs/v2/modifiers/flip/
    const flip = {
        name: 'flip',
        options: {
            fallbackPlacements,
        },
    };
    // Disable the scroll and resize listeners when the popover is closed.
    // https://popper.js.org/docs/v2/modifiers/event-listeners/
    const eventListeners = {
        name: 'eventListeners',
        options: { scroll: isOpen, resize: isOpen },
    };
    // Note: the usePopper hook intentionally takes a DOM node, not a ref, in
    // order to update when the node changes. We use a callback ref for this.
    const [popperElement, setPopperElement] = useState(null);
    const { styles, attributes, update } = usePopper(triggerEl.current, popperElement, {
        strategy: 'fixed',
        placement,
        modifiers: [mobilePosition, flip, eventListeners, ...modifiers],
    });
    // This is a performance optimization to prevent event listeners from being
    // re-attached on every render.
    const popperRef = useLatest(popperElement);
    useEscapeKey(() => handleToggle(false), isOpen);
    useClickOutside(popperRef, () => handleToggle(false), isOpen);
    const prevOpen = usePrevious(isOpen);
    useEffect(() => {
        if (update) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            update();
        }
        // Focus the first or last element after opening
        if (!prevOpen && isOpen) {
            const element = (triggerKey.current && triggerKey.current === 'ArrowUp'
                ? menuEl.current && menuEl.current.lastElementChild
                : menuEl.current && menuEl.current.firstElementChild);
            if (element) {
                element.focus();
            }
        }
        // Focus the trigger button after closing
        if (prevOpen && !isOpen) {
            const triggerButton = (triggerEl.current &&
                triggerEl.current.firstElementChild);
            triggerButton.focus();
        }
        triggerKey.current = null;
    }, [isOpen, prevOpen, update]);
    const focusProps = useFocusList();
    const handleTriggerClick = (event) => {
        // This prevents the event from bubbling which would trigger the
        // useClickOutside above and would prevent the popover from closing.
        event.stopPropagation();
        handleToggle((prev) => !prev);
    };
    const handleTriggerKeyDown = (event) => {
        if (isArrowDown(event)) {
            triggerKey.current = 'ArrowDown';
            handleToggle(true);
        }
        if (isArrowUp(event)) {
            triggerKey.current = 'ArrowUp';
            handleToggle((prev) => !prev);
        }
    };
    const handlePopoverItemClick = (event, onClick) => {
        if (onClick) {
            onClick(event);
        }
        handleToggle(false);
    };
    return (_jsxs(Fragment, { children: [_jsx(TriggerElement, { ref: triggerEl, children: _jsx(Component, { id: triggerId, "aria-haspopup": true, "aria-controls": menuId, "aria-expanded": isOpen, onClick: handleTriggerClick, onKeyDown: handleTriggerKeyDown }, void 0) }, void 0), _jsxs(Portal, { children: [_jsx(Overlay, { isOpen: isOpen, style: { zIndex: zIndex || theme.zIndex.popover } }, void 0), _jsx(Popper, { ...props, ref: setPopperElement, isOpen: isOpen, style: styles.popper, ...attributes.popper, children: _jsx(PopoverMenu, { id: menuId, ref: menuEl, isOpen: isOpen, "aria-labelledby": triggerId, role: "menu", children: actions.map((action, index) => isDivider(action) ? (_jsx(Hr, { css: dividerStyles }, index)) : (_jsx(PopoverItem, { ...action, ...focusProps, onClick: (event) => handlePopoverItemClick(event, action.onClick) }, index))) }, void 0) }, void 0)] }, void 0)] }, void 0));
};
